/**
 * Partners directory — v1 is a static array; v1.1 reads from a Sidequester
 * `partners` table and tracks click-through via a `partner_clicks` table so
 * we can attribute PartnerStack referrals.
 *
 * The shape mirrors what we'd store per row, so swapping in the API later is
 * a one-line change.
 */

interface Partner {
  slug: string;
  name: string;
  category: string;
  blurb: string;
  offer: string;
  /** Replaced with a real PartnerStack referral URL when wired up. */
  ctaUrl: string;
}

const PARTNERS: Partner[] = [
  {
    slug: "xero",
    name: "Xero",
    category: "Accounting",
    blurb:
      "Cloud accounting designed for small businesses. Alternative to FreeAgent — same dashboard, different source.",
    offer: "Discounted plans via Bloxx — set up with our PartnerStack link.",
    ctaUrl: "https://www.xero.com",
  },
  {
    slug: "aircall",
    name: "Aircall",
    category: "Phone system",
    blurb:
      "Cloud phone for distributed teams. Solid choice if you want a real business number without the hassle.",
    offer: "Bloxx partner discount on the first three months.",
    ctaUrl: "https://aircall.io",
  },
  {
    slug: "webflow",
    name: "Webflow",
    category: "Website builder",
    blurb:
      "Build and host a no-code marketing site. Great for service-business landing pages and case studies.",
    offer: "Bloxx partner discount on annual plans.",
    ctaUrl: "https://webflow.com",
  },
  {
    slug: "airwallex",
    name: "Airwallex",
    category: "Banking & FX",
    blurb:
      "Multi-currency business account. Useful for ecom or anyone invoicing in USD/EUR.",
    offer: "Free FX credit when you onboard via Bloxx.",
    ctaUrl: "https://airwallex.com",
  },
  {
    slug: "miro",
    name: "Miro",
    category: "Collaboration",
    blurb: "Visual whiteboard for planning, mapping, and async working.",
    offer: "Extended trial via Bloxx partnership.",
    ctaUrl: "https://miro.com",
  },
  {
    slug: "signable",
    name: "Signable",
    category: "E-signatures",
    blurb:
      "UK-built electronic signatures. Used by accountants and contractors who need clean engagement letters.",
    offer: "Discounted starter plan for Bloxx users.",
    ctaUrl: "https://www.signable.co.uk",
  },
];

export default function Partners() {
  return (
    <section className="space-y-8">
      <header>
        <h1 className="text-3xl font-semibold">Partners</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Tools we use ourselves, with discounts negotiated for Bloxx users. We
          earn a referral fee when you sign up via one of these links — same
          price for you, helps fund this dashboard.
        </p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2">
        {PARTNERS.map((p) => (
          <PartnerCard key={p.slug} partner={p} />
        ))}
      </div>

      <p className="text-xs text-muted-foreground">
        Heading to PartnerStack to manage these properly — discounts and links
        will update automatically once that's wired up.
      </p>
    </section>
  );
}

function PartnerCard({ partner }: { partner: Partner }) {
  return (
    <article className="flex h-full flex-col rounded-lg border p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{partner.name}</h2>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            {partner.category}
          </p>
        </div>
      </div>
      <p className="mt-3 text-sm text-muted-foreground">{partner.blurb}</p>
      <div className="mt-3 rounded-md bg-secondary/40 px-3 py-2 text-xs">
        {partner.offer}
      </div>
      <a
        href={partner.ctaUrl}
        target="_blank"
        rel="noreferrer"
        className="mt-4 inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
      >
        Visit {partner.name} →
      </a>
    </article>
  );
}
